import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Icon } from '@tviklax/ui-components';
import { ShipmentFunnnelStep } from './types';

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: ${props => props.theme.shadows.md};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const Title = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.gray800};
`;

const FunnelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px; /* Increased gap for touch targets */
`;

const StepBar = styled(motion.div) <{ $width: string; $index: number; $isBottleneck: boolean }>`
  background: ${props => props.$isBottleneck ? props.theme.colors.error : props.theme.colors.primary};
  height: 40px;
  width: ${props => props.$width};
  opacity: ${props => 1 - (props.$index * 0.12)}; /* Progressing opacity */
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  color: white;
  font-size: 0.875rem;
  font-weight: 700;
  position: relative;
`;

// StepLabel removed as it was unused

const StageName = styled.span`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.gray600};
  font-weight: 500;
  line-height: 1;
`;

const SlaWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: ${props => props.theme.colors.error};
  margin-top: 4px;
  font-weight: 600;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const AvgTime = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.gray500};
  line-height: 1;
`;

const ConversionLabel = styled.div`
  position: absolute;
  left: 50%;
  bottom: -14px;
  transform: translateX(-50%);
  font-size: 0.65rem;
  color: ${props => props.theme.colors.gray500};
  background: white;
  padding: 0 4px;
  font-weight: 600;
  z-index: 1;
`;

const StepArrow = styled.div`
  position: absolute;
  right: -12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.gray300};
  font-size: 1rem;
  pointer-events: none;
`;

interface ShipmentFunnelProps {
  steps: ShipmentFunnnelStep[];
}

export const ShipmentFunnel: React.FC<ShipmentFunnelProps> = ({ steps }) => {
  if (steps.length === 0) {
    return (
      <Card>
        <Header>
          <Icon name="Package" color="#f86400" size={20} />
          <Title>Shipment Funnel</Title>
        </Header>
        <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No live shipment data yet.</div>
      </Card>
    );
  }

  const rawMax = Math.max(...steps.map(s => s.count));
  const maxCount = rawMax > 0 ? rawMax : 1; // avoid division by zero

  return (
    <Card>
      <Header>
        <Icon name="Package" color="#f86400" size={20} />
        <Title>Shipment Funnel</Title>
      </Header>
      <FunnelContainer>
        {steps.map((step, index) => {
          const width = rawMax > 0 ? `${(step.count / maxCount) * 100}%` : `0%`;
          let conversion: number | null = null;
          if (index > 0) {
            const prev = steps[index - 1].count || 0;
            if (prev > 0) {
              const val = (step.count / prev) * 100;
              conversion = Number.isFinite(val) ? Math.round(val) : null;
            } else {
              conversion = null;
            }
          }

          return (
            <div key={step.stage} style={{ marginBottom: '16px', position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '4px' }}>
                <StageName>{step.stage}</StageName>
                {step.avgTime && <AvgTime>Avg: {step.avgTime}</AvgTime>}
              </div>
              <div style={{ position: 'relative' }}>
                <StepBar
                  $width={width}
                  $index={index}
                  $isBottleneck={!!step.slaWarning}
                  initial={{ width: 0 }}
                  animate={{ width }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                >
                  <span>{step.count}</span>
                </StepBar>
                {index < steps.length - 1 && <StepArrow>→</StepArrow>}
                {conversion !== null && Number.isFinite(conversion) && (
                  <ConversionLabel>{conversion}% conversion</ConversionLabel>
                )}
              </div>
              {step.slaWarning && (
                <SlaWarning>
                  <Icon name="AlertCircle" />
                  {step.slaWarning}
                </SlaWarning>
              )}
            </div>
          );
        })}
      </FunnelContainer>
    </Card>
  );
};
